import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Divider,
  Chip,
  ButtonGroup,
  CircularProgress,
  Skeleton,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Badge as BadgeIcon,
  DateRange as DateRangeIcon,
  FilterAltOutlined as FilterIcon,
  LocalHospitalOutlined as InsuranceIcon,
} from '@mui/icons-material';
import { loadPatients, insuranceOptions } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';
import PatientVitals from './PatientVitals';
import PatientIDCard from './Patientidcard';

import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';

// ─── Helper: did this patient register today? ────────────────────────────────
// Handles: YYYY-MM-DD, YYYY-MM-DD HH:mm:ss, DD-MM-YYYY, DD/MM/YYYY, MM/DD/YYYY
const isRegisteredToday = (dateStr) => {
  if (!dateStr) return false;

  const today = new Date();
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1; // 1-based
  const td = today.getDate();

  const s = String(dateStr).trim();

  // YYYY-MM-DD or YYYY-MM-DD HH:mm:ss  (MySQL default)
  const isoMatch = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return (
      parseInt(isoMatch[1]) === ty &&
      parseInt(isoMatch[2]) === tm &&
      parseInt(isoMatch[3]) === td
    );
  }

  // DD-MM-YYYY or DD/MM/YYYY
  const dmyMatch = s.match(/^(\d{2})[-\/](\d{2})[-\/](\d{4})/);
  if (dmyMatch) {
    return (
      parseInt(dmyMatch[3]) === ty &&
      parseInt(dmyMatch[2]) === tm &&
      parseInt(dmyMatch[1]) === td
    );
  }

  // Fallback: let JS parse it
  try {
    const reg = new Date(s);
    if (!isNaN(reg)) {
      return (
        reg.getFullYear() === ty &&
        reg.getMonth() + 1 === tm &&
        reg.getDate() === td
      );
    }
  } catch (_) {}

  return false;
};

const Home = ({ onAddNewPatient, onEditPatient }) => {

  const [searchBy, setSearchBy] = useState({
    opdNo: true,
    name: false,
    insuranceNo: false,
    phoneNo: false,
    fingerPrint: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [activeFilter, setActiveFilter] = useState('today');
  const [isLoading, setIsLoading] = useState(true);
  const [allPatientData, setallPatientData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  // Custom date-range filter. Only the "Date range" preset uses these.
  const [rangeFrom, setRangeFrom] = useState('');
  const [rangeTo,   setRangeTo]   = useState('');

  // Insurance scheme filter. '' = every scheme; '__none__' = patients with
  // no scheme recorded. Stacks on top of the period / date-range filter,
  // so "Apex, this week" and "Non Insured, 01–15 Aug" both work.
  const [insuranceFilter, setInsuranceFilter] = useState('');
  const [insuranceList,   setInsuranceList]   = useState([]);   // master list
  const [insuranceCounts, setInsuranceCounts] = useState([]);   // for the period

  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [openPatient, setOpenPatient] = useState(false);
  const [vitalsInfo, setVitalsInfo] = useState(null);

  // ── ID Card state ──────────────────────────────────────────────────────────
  const [openIDCard, setOpenIDCard] = useState(false);
  const [idCardPatient, setIdCardPatient] = useState(null);

  // Read company info once from localStorage
  const companyInfo = (() => {
    try { return JSON.parse(localStorage.getItem('companyInfo') || '{}'); }
    catch { return {}; }
  })();

  // Loading skeleton component for table rows
  const LoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width={60} /></TableCell>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} /></TableCell>
          <TableCell><Skeleton variant="text" width={40} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={110} height={20} sx={{ borderRadius: 1 }} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={90} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </>
  );

  useEffect(() => {
    // Only fire the range fetch once at least one bound is set, otherwise
    // we'd hit the backend with no constraints every time the picker
    // gains focus.
    if (activeFilter === 'range' && !rangeFrom && !rangeTo) return;
    AllPatient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, entriesPerPage, activeSearchTerm, rangeFrom, rangeTo, insuranceFilter]);

  // Master scheme list — fetched once. Kept separate from the per-period
  // counts so a scheme with no visits today is still selectable.
  useEffect(() => {
    let alive = true;
    axios.get(insuranceOptions)
      .then(r => { if (alive && r.data?.success) setInsuranceList(r.data.results || []); })
      .catch(() => { /* dropdown falls back to the counts below */ });
    return () => { alive = false; };
  }, []);

  const AllPatient = async () => {
    try {
      setIsLoading(true);
      const activeSearchFields = Object.keys(searchBy).filter(k => searchBy[k]).join(',');
      const params = new URLSearchParams({
        filter: activeFilter,
        limit: entriesPerPage,
        search: activeSearchTerm,
        searchBy: activeSearchFields
      });
      if (activeFilter === 'range') {
        if (rangeFrom) params.set('from', rangeFrom);
        if (rangeTo)   params.set('to',   rangeTo);
      }
      if (insuranceFilter) params.set('insurance', insuranceFilter);
      const response = await axios.get(`${loadPatients}?${params}`);
      if (response.data.success) {
        setallPatientData(response.data.results);
        setTotalRecords(response.data.total || response.data.results.length);
        setInsuranceCounts(response.data.insuranceBreakdown || []);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed Load Data',
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Dropdown options = every configured scheme UNION every scheme seen in
  // the current period, each carrying its count for that period. A scheme
  // with no visits still lists (showing 0) so the user can tell "nobody
  // came on Apex today" apart from "Apex isn't set up here".
  const insuranceMenu = useMemo(() => {
    const counts = new Map();
    insuranceCounts.forEach(r => counts.set(String(r.insurance), Number(r.total) || 0));

    const names = new Set(insuranceList.map(r => String(r.name)));
    counts.forEach((_, k) => { if (k !== '__none__') names.add(k); });

    const opts = [...names]
      .map(name => ({ value: name, label: name, count: counts.get(name) || 0 }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));

    // Patients registered without a scheme — real records, and invisible
    // from this screen unless they're selectable.
    const noneCount = counts.get('__none__') || 0;
    if (noneCount > 0) {
      opts.push({ value: '__none__', label: 'Not recorded', count: noneCount });
    }
    return opts;
  }, [insuranceList, insuranceCounts]);

  const insuranceTotal = useMemo(
    () => insuranceCounts.reduce((n, r) => n + (Number(r.total) || 0), 0),
    [insuranceCounts]
  );

  const labelForInsurance = (v) =>
    v === '__none__' ? 'Not recorded' : v;

  const handleSearchByChange = (field) => {
    setSearchBy(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSearch = () => { setActiveSearchTerm(searchTerm); };
  const handleClearSearch = () => { setSearchTerm(''); setActiveSearchTerm(''); };
  const handleSearchKeyPress = (e) => { if (e.key === 'Enter') handleSearch(); };

  const handleRowSelect = (patient) => {
    setSelectedPatient(patient);
    const next = new Set(selectedPatients);
    selectedPatients.has(patient.id) ? next.delete(patient.id) : next.add(patient.id);
    setSelectedPatients(next);
  };

  const handleClearSelection = () => { setSelectedPatients(new Set()); setSelectedPatient(null); };

  const handleSelectAll = () => {
    if (selectedPatients.size === allPatientData.length) {
      handleClearSelection();
    } else {
      setSelectedPatients(new Set(allPatientData.map(p => p.id)));
    }
  };

  const handleEditRecord = (patient) => {
    if (onEditPatient) onEditPatient(patient);
  };

  const handleOpenVitals = (patient) => {
    setVitalsInfo(patient);
    setOpenPatient(true);
  };

  const handleClosePatient = () => { setOpenPatient(false); setVitalsInfo(null); };
  const handleAddNewPatient = () => { if (onAddNewPatient) onAddNewPatient(); };

  // ── ID Card handlers ───────────────────────────────────────────────────────
  const handleOpenIDCard = (patient) => { setIdCardPatient(patient); setOpenIDCard(true); };
  const handleCloseIDCard = () => { setOpenIDCard(false); setIdCardPatient(null); };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
            Medical records
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#64748b' }}>
            Welcome to the Hospital Records System. Below is the list of your patients:
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddNewPatient}
          disabled={isLoading}
          sx={{
            backgroundColor: '#059669', color: 'white',
            fontSize: '16px', fontWeight: 600, py: 2, px: 4,
            borderRadius: 2, textTransform: 'none',
            boxShadow: '0 4px 16px rgba(5,150,105,0.3)',
            '&:hover': { backgroundColor: '#047857', boxShadow: '0 6px 20px rgba(5,150,105,0.4)' },
            '&:disabled': { backgroundColor: '#9ca3af', boxShadow: 'none' },
          }}
        >
          Add New Patient
        </Button>
      </Box>

      {/* Selection alert */}
      {selectedPatients.size > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' } }}
          action={
            <Button color="inherit" size="small" onClick={handleClearSelection} startIcon={<ClearIcon />} sx={{ textTransform: 'none' }}>
              Clear Selection
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon fontSize="small" />
            <Typography>{selectedPatients.size} patient{selectedPatients.size !== 1 ? 's' : ''} selected</Typography>
          </Box>
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#374151', fontWeight: 600 }}>
            Search & Filter Patients
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { key: 'opdNo', label: 'OPD No.' },
                { key: 'name', label: 'Name' },
                { key: 'insuranceNo', label: 'Insurance No.' },
                { key: 'phoneNo', label: 'Phone No.' },
              ].map(item => (
                <FormControlLabel
                  key={item.key}
                  control={<Checkbox checked={searchBy[item.key]} onChange={() => handleSearchByChange(item.key)} disabled={isLoading} sx={{ color: '#059669' }} />}
                  label={item.label}
                  sx={{ '& .MuiFormControlLabel-label': { color: '#374151', fontWeight: 500 } }}
                />
              ))}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center' }} />

            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#475569' }}>
                <FilterIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Period
                </Typography>
              </Box>
              {[
                { key: 'today',  label: 'Today' },
                { key: 'week',   label: 'This Week' },
                { key: 'month',  label: 'This Month' },
                { key: 'year',   label: 'This Year' },
                { key: 'all',    label: 'All' },
                { key: 'range',  label: 'Date Range', icon: <DateRangeIcon sx={{ fontSize: 14, mr: 0.5 }} /> },
              ].map(f => {
                const active = activeFilter === f.key;
                return (
                  <Button
                    key={f.key}
                    onClick={() => setActiveFilter(f.key)}
                    disabled={isLoading}
                    size="small"
                    disableElevation
                    sx={{
                      px: 1.5, py: 0.55,
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: 999,
                      border: '1px solid',
                      borderColor: active ? '#059669' : '#e2e8f0',
                      bgcolor: active ? '#059669' : '#fff',
                      color: active ? '#fff' : '#475569',
                      '&:hover': {
                        bgcolor: active ? '#047857' : '#f1f5f9',
                        borderColor: active ? '#047857' : '#cbd5e1',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {f.icon}
                    {f.label}
                  </Button>
                );
              })}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center' }} />

            {/* Insurance scheme filter — combines with the period above. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#475569' }}>
                <InsuranceIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Insurance
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <Select
                  value={insuranceFilter}
                  onChange={(e) => setInsuranceFilter(e.target.value)}
                  disabled={isLoading}
                  displayEmpty
                  renderValue={(v) => (v ? labelForInsurance(v) : 'All insurances')}
                  sx={{
                    borderRadius: 2,
                    bgcolor: insuranceFilter ? '#ecfdf5' : '#fff',
                    fontSize: '0.82rem',
                    fontWeight: insuranceFilter ? 700 : 500,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: insuranceFilter ? '#059669' : '#e2e8f0',
                    },
                  }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 420 } } }}
                >
                  <MenuItem value="">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                      <span>All insurances</span>
                      <Typography component="span" variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
                        {insuranceTotal}
                      </Typography>
                    </Box>
                  </MenuItem>
                  {insuranceMenu.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                        <span style={{ fontStyle: opt.value === '__none__' ? 'italic' : 'normal' }}>
                          {opt.label}
                        </span>
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: opt.count ? '#059669' : '#cbd5e1', fontWeight: 700 }}
                        >
                          {opt.count}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {insuranceFilter && (
                <Tooltip title="Clear insurance filter" arrow>
                  <IconButton size="small" onClick={() => setInsuranceFilter('')} disabled={isLoading} sx={{ color: '#64748b' }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Per-scheme totals for the selected period. Click a chip to
              filter, click it again to clear — the counts stay put because
              the backend computes them before applying the insurance
              filter. */}
          {!isLoading && insuranceCounts.length > 0 && (
            <Box sx={{ mb: 2.5, mt: -0.5, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5 }}>
                Registered {activeFilter === 'today' ? 'today' : 'in this period'}
              </Typography>
              {insuranceMenu.filter(o => o.count > 0).map(opt => {
                const active = insuranceFilter === opt.value;
                return (
                  <Chip
                    key={opt.value}
                    label={`${opt.label} · ${opt.count}`}
                    size="small"
                    onClick={() => setInsuranceFilter(active ? '' : opt.value)}
                    sx={{
                      height: 24,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      bgcolor: active ? '#059669' : '#f1f5f9',
                      color:   active ? '#fff'    : '#475569',
                      border: '1px solid',
                      borderColor: active ? '#059669' : '#e2e8f0',
                      '&:hover': { bgcolor: active ? '#047857' : '#e2e8f0' },
                    }}
                  />
                );
              })}
            </Box>
          )}

          {/* Custom date-range row: only shown when the Date Range chip
              above is selected. Each picker fires a request the moment
              its value changes (AllPatient effect depends on these). */}
          {activeFilter === 'range' && (
            <Box
              sx={{
                mb: 2.5, mt: -0.5,
                p: 1.5, borderRadius: 2,
                bgcolor: '#f1f5f9',
                border: '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#475569' }}>
                <DateRangeIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                  Date range
                </Typography>
              </Box>
              <TextField
                size="small"
                type="date"
                label="From"
                value={rangeFrom}
                onChange={(e) => setRangeFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: rangeTo || undefined }}
                disabled={isLoading}
                sx={{ minWidth: 170, bgcolor: '#fff', borderRadius: 1 }}
              />
              <TextField
                size="small"
                type="date"
                label="To"
                value={rangeTo}
                onChange={(e) => setRangeTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: rangeFrom || undefined }}
                disabled={isLoading}
                sx={{ minWidth: 170, bgcolor: '#fff', borderRadius: 1 }}
              />
              {(rangeFrom || rangeTo) && (
                <Button
                  onClick={() => { setRangeFrom(''); setRangeTo(''); }}
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: 'none', color: '#64748b', fontWeight: 600 }}
                >
                  Clear dates
                </Button>
              )}
              {!rangeFrom && !rangeTo && (
                <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                  Pick at least one date to filter
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              variant="outlined"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              disabled={isLoading}
              sx={{ flexGrow: 1, minWidth: 300, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={handleClearSearch} disabled={isLoading} sx={{ color: '#64748b' }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                backgroundColor: '#059669', color: 'white', borderRadius: 2, px: 3, py: 1.5, fontWeight: 600, textTransform: 'none',
                '&:hover': { backgroundColor: '#047857', boxShadow: '0 4px 12px rgba(5,150,105,0.3)' },
                '&:disabled': { backgroundColor: '#9ca3af', color: '#e5e7eb' },
              }}
            >
              Search
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#374151', fontWeight: 500 }}>Show</Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={entriesPerPage} onChange={e => setEntriesPerPage(e.target.value)} disabled={isLoading} sx={{ borderRadius: 2 }}>
                  {[10, 20, 50, 100, 500, 1000].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </Select>
              </FormControl>
              <Typography sx={{ color: '#374151', fontWeight: 500 }}>entries</Typography>
            </Box>
          </Box>

          {activeSearchTerm && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>Searching for:</Typography>
              <Chip label={activeSearchTerm} onDelete={handleClearSearch} size="small" sx={{ backgroundColor: '#dbeafe', color: '#1e40af', fontWeight: 500 }} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Patient Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            maxHeight: 'calc(100vh - 380px)',
            // Compact, professional table styling.
            '& .MuiTableCell-root': {
              fontSize: '0.82rem',
              py: 1,
              borderBottom: '1px solid #f1f5f9',
            },
            '& .MuiTableCell-head': {
              fontSize: '0.7rem',
              fontWeight: 800,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
              color: '#475569',
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              py: 1.25,
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedPatients.size > 0 && selectedPatients.size < allPatientData.length}
                    checked={allPatientData.length > 0 && selectedPatients.size === allPatientData.length}
                    onChange={handleSelectAll}
                    disabled={isLoading || allPatientData.length === 0}
                    size="small"
                    sx={{ color: '#059669' }}
                  />
                </TableCell>
                <TableCell>Patient ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Gender</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>Insurance</TableCell>
                <TableCell>Insurance #</TableCell>
                {/*  Beside the membership number, matching Billing & Accounts:
                     a claim needs both, and an officer checking one is checking
                     the other. */}
                <TableCell>CCC</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Telephone</TableCell>
                <TableCell>Occupation</TableCell>
                <TableCell>Visit Date</TableCell>
                <TableCell sx={{ textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <LoadingSkeleton />
              ) : allPatientData.length === 0 ? (
                <TableRow>
                  {/*  13 now: checkbox, ID, Name, Gender, Age, Insurance,
                       Insurance #, CCC, Address, Telephone, Occupation, Visit
                       Date, Actions. A colSpan short of the header boxes the
                       "no patients" message into part of the width. */}
                  <TableCell colSpan={13} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '16px' }}>
                      {activeSearchTerm
                        ? 'No patients found matching your search criteria'
                        : insuranceFilter
                          ? `No patients on ${labelForInsurance(insuranceFilter)} for this period`
                          : 'No patient records found for this period'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                allPatientData.map((patient) => {
                  const canPrintID = isRegisteredToday(patient.reg_date);
                  return (
                    <TableRow
                      key={patient.id}
                      selected={selectedPatients.has(patient.id)}
                      sx={{
                        '&:hover': { backgroundColor: '#f8fafc' },
                        '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                        '&.Mui-selected': {
                          backgroundColor: '#ecfdf5 !important',
                          '&:hover': { backgroundColor: '#d1fae5 !important' },
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={selectedPatients.has(patient.id)} onChange={() => handleRowSelect(patient)} size="small" sx={{ color: '#059669' }} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#3b82f6', fontVariantNumeric: 'tabular-nums' }}>{patient.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{patient.fullName}</TableCell>
                      <TableCell>
                        {patient.gender && (
                          <Chip
                            label={patient.gender}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              letterSpacing: 0.3,
                              backgroundColor: patient.gender === 'Male' || patient.gender === 'MALE' ? '#dbeafe' : '#fce7f3',
                              color: patient.gender === 'Male' || patient.gender === 'MALE' ? '#1d4ed8' : '#be185d',
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{patient.age}</TableCell>
                      <TableCell>
                        {patient.type
                          ? <Tooltip title={patient.type} arrow>
                              <Chip
                                label={patient.type}
                                size="small"
                                sx={{
                                  height: 20,
                                  maxWidth: 170,
                                  fontSize: '0.68rem',
                                  fontWeight: 700,
                                  backgroundColor: /non\s*insured/i.test(patient.type) ? '#f1f5f9' : '#ecfdf5',
                                  color:           /non\s*insured/i.test(patient.type) ? '#475569' : '#047857',
                                }}
                              />
                            </Tooltip>
                          : <span style={{ color: '#cbd5e1' }}>—</span>}
                      </TableCell>
                      <TableCell>{patient.Insurance || <span style={{ color: '#cbd5e1' }}>—</span>}</TableCell>
                      {/*  The claim check code for THIS VISIT. It is not a
                           property of the patient — it comes off the member's
                           card on the day, and lives on claim_information — so
                           a patient seen twice can legitimately carry one on one
                           visit and not the other.

                           The same em dash this table already uses for a missing
                           insurance number, so an absent code reads as absent
                           rather than as a rendering fault. That absence is
                           exactly what medical records are scanning for. */}
                      <TableCell sx={{
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: patient.ccc ? 600 : 400,
                        color: patient.ccc ? '#0f172a' : undefined,
                      }}>
                        {patient.ccc || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </TableCell>
                      <TableCell sx={{ color: '#475569' }}>{patient.res}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: '#475569' }}>{patient.tel}</TableCell>
                      <TableCell sx={{ color: '#475569' }}>{patient.occ}</TableCell>
                      <TableCell sx={{ color: '#059669', fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{patient.reg_date}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'inline-flex', gap: 0.5, justifyContent: 'center' }}>

                          {/* Vitals */}
                          <Tooltip title={`View ${patient.fullName}'s vitals`} arrow>
                            <IconButton
                              onClick={() => handleOpenVitals(patient)}
                              size="small"
                              sx={{
                                bgcolor: '#fef3f2', color: '#dc2626',
                                '&:hover': { bgcolor: '#fee2e2', color: '#b91c1c' },
                                borderRadius: 1.5, p: 0.65,
                              }}
                            >
                              <DeviceThermostatOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {/* Edit */}
                          <Tooltip title={`Edit ${patient.fullName}'s record`} arrow>
                            <IconButton
                              onClick={() => handleEditRecord(patient)}
                              size="small"
                              sx={{
                                bgcolor: '#f0f9ff', color: '#0ea5e9',
                                '&:hover': { bgcolor: '#e0f2fe', color: '#0284c7' },
                                borderRadius: 1.5, p: 0.65,
                              }}
                            >
                              <EditIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {/* ── ID Card (only for today's registrations) ── */}
                          <Tooltip
                            title={canPrintID ? `Print ID card for ${patient.fullName}` : 'ID card only available on registration day'}
                            arrow
                          >
                            <span>   {/* span needed so Tooltip works on disabled button */}
                              <IconButton
                                onClick={() => canPrintID && handleOpenIDCard(patient)}
                                disabled={!canPrintID}
                                size="small"
                                sx={{
                                  bgcolor: canPrintID ? '#f0fdf4' : '#f8fafc',
                                  color:   canPrintID ? '#16a34a' : '#cbd5e1',
                                  '&:hover': canPrintID ? { bgcolor: '#dcfce7', color: '#15803d' } : {},
                                  '&:disabled': { bgcolor: '#f8fafc', color: '#cbd5e1' },
                                  borderRadius: 1.5, p: 0.65,
                                  transition: 'all 0.2s',
                                }}
                              >
                                <BadgeIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>

                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2.5, py: 1.5, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
          {isLoading ? (
            <>
              <CircularProgress size={14} sx={{ color: '#059669' }} />
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}>
                Loading patient records…
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}>
                Showing <strong style={{ color: '#0f172a' }}>{allPatientData.length}</strong> of <strong style={{ color: '#0f172a' }}>{totalRecords}</strong> entries
              </Typography>
              {selectedPatients.size > 0 && (
                <Chip
                  label={`${selectedPatients.size} selected`}
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#059669', color: 'white' }}
                />
              )}
              {activeFilter !== 'today' && activeFilter !== 'range' && (
                <Chip
                  label={
                    activeFilter === 'week'   ? 'This week'  :
                    activeFilter === 'month'  ? 'This month' :
                    activeFilter === 'year'   ? 'This year'  :
                    activeFilter === 'all'    ? 'All time'   : activeFilter
                  }
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#0ea5e9', color: 'white' }}
                />
              )}
              {activeFilter === 'range' && (rangeFrom || rangeTo) && (
                <Chip
                  icon={<DateRangeIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                  label={`${rangeFrom || '…'} → ${rangeTo || '…'}`}
                  size="small"
                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#0ea5e9', color: 'white' }}
                />
              )}
              {insuranceFilter && (
                <Chip
                  icon={<InsuranceIcon sx={{ fontSize: 14, color: 'white !important' }} />}
                  label={`${labelForInsurance(insuranceFilter)} — ${totalRecords} patient${totalRecords === 1 ? '' : 's'}`}
                  onDelete={() => setInsuranceFilter('')}
                  size="small"
                  sx={{
                    height: 22, fontSize: '0.7rem', fontWeight: 700,
                    bgcolor: '#059669', color: 'white',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } },
                  }}
                />
              )}
            </>
          )}
        </Box>
      </Card>

      {/* Vitals Dialog */}
      <PatientVitals open={openPatient} onClose={handleClosePatient} info={vitalsInfo} />

      {/* ── ID Card Dialog ─────────────────────────────────────────────────── */}
      <PatientIDCard
        open={openIDCard}
        onClose={handleCloseIDCard}
        patient={idCardPatient}
        companyInfo={companyInfo}
      />
    </Box>
  );
};

export default Home;